/**
 * @fileoverview The change dialog of one packet or plant row (D78 rev 2 §3, mock-up frame 3): a bottom sheet over the
 * row, "Change this packet" (a several-kinds row: "Change this plant"). Top to bottom: the row's picture and, for a
 * packet, what the packet says; what Grow thinks a packet it could not place is, with one Yes (the caller's
 * suggestion); the one set of plant fields the app has, with its search (PlantIdentityFields: "Not in Grow's list?
 * Describe it", Plant, Type with B52's "Not sure" first, Variety with "Add your own variety…", and for a kinds row the
 * variety Grow saw); the new variety's name when the row's variety is a new one; its Bed ("Same as the others (Back
 * bed)", "A new bed…" with its name box, their beds); Done; and "Remove this packet" / "Remove this plant".
 *
 * Every field is a choice kept as it is made, so closing the sheet keeps what was changed: there is nothing to
 * cancel, and Done just folds it. Done is off while the row has no plant and is not on Type "Not sure".
 *
 * Why a sheet and not fields inside the row: on a phone a card is a list of four to thirty rows under a photo strip;
 * fields opening inside a row pushed the rest off the screen and the button with them (the 2026-10-03 critique).
 *
 * A kept seed (D69 an emailed order's, B137 a packet kept from a photo; features/seed-orders HeldSeeds) uses the same sheet
 * with no Bed field (it goes to the Seed vault: showBed false), "Not a seed" as its remove words, and a foot of Cancel (left,
 * outline) and its action, "Plan to grow it", which files it (doneLabel, onDone, cancelable: the shared ActionRow, rule 7).
 *
 * Where: PacketPile and KindsPile (photo-tray), PacketReview, HeldSeeds (the Seed vault).
 * API: none of its own; PlantIdentityFields' (the plant picker's answers, GET /plants/picker/crops, /types and
 * /varieties, asked as the fields are used; POST /plants/identify, POST /varieties/own).
 * Settings: the server's limits.LOCATION_NAME_MAX and limits.VARIETY_NAME_MAX (lib/growSettings).
 * Words (en/es): packets.bed, packets.newBed, packets.bedName, packets.newVariety, packets.done,
 * tray.kinds.sameAsOthers; the title and the remove words are the caller's (packets.changeTitle, packets.removeThis,
 * tray.kinds.changeTitle, tray.kinds.removeThis).
 *
 * @module RowChangeDialog
 */

import React from 'react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Input } from '@/shared/components/ui/input';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { PlantIdentityFields, type SeenVarietyOption } from '@/shared/components/plant-fields/PlantIdentityFields';
import type { CropChoice } from '@/shared/components/plant-fields/usePlantPicker';
import type { BedName } from '@/shared/api/locations';
import type { OwnBed } from '../lib/packetRows';

/** The Bed field's choice "the same as the others". */
const SAME = 'same';
/** The Bed field's choice "A new bed…". */
const NEW = 'new';

/** What the dialog is given. */
interface RowChangeDialogProps {
  /** Shown. */
  open: boolean;
  /** Folds it (×, Done, a tap outside): what was changed is kept. */
  onClose: () => void;
  /** "Change this packet" or "Change this plant". */
  title: string;
  /** The row's picture. */
  picture: React.ReactNode;
  /** The row's name now, beside the picture. */
  name: string;
  /** Under it: what the packet says ("Packet says Tomato · Cherokee Purple"); none for a kinds row. */
  caption?: string | null;
  /** Above the fields: what Grow thinks an unplaced packet is, with Yes; none otherwise. */
  suggestion?: React.ReactNode;
  /** The row's plant, type and variety. */
  value: CropChoice;
  /** A change to them. */
  onChange: (next: CropChoice) => void;
  /** A variety Grow saw, offered in the Variety list (a kinds row); none for a packet. */
  seen?: SeenVarietyOption | null;
  /** The new variety's name, when the row's variety is a new one; null for none. */
  newVariety?: { value: string; onChange: (name: string) => void } | null;
  /** The bed the others go to, for "Same as the others (Back bed)". */
  sharedBed: string;
  /** The row's own bed, or null for the same as the others. */
  bed: OwnBed | null;
  /** A change to it. */
  onBed: (bed: OwnBed | null) => void;
  /** Their live beds. */
  beds: readonly BedName[];
  /** "Remove this packet" or "Remove this plant". */
  removeLabel: string;
  /** Takes the row out. */
  onRemove: () => void;
  /** Done is off: the row has no plant yet. */
  doneDisabled: boolean;
  /** For the fields' ids. */
  idPrefix: string;
  /** The Bed field: shown for a packet or a plant row; off for a held seed, which always goes to the Seed vault (D69). */
  showBed?: boolean;
  /** Done's words when it does more than fold the sheet (a held seed's Save, D69); "Done" otherwise. */
  doneLabel?: string;
  /** What Done does (a held seed's Save files it, D69); folding the sheet otherwise. */
  onDone?: () => void;
  /** The foot is Cancel (left, outline) and the action (rule 7, the shared ActionRow), with the remove words far left; Cancel folds the sheet. */
  cancelable?: boolean;
}

/**
 * The sheet that changes one row.
 *
 * @param props - RowChangeDialogProps.
 */
export const RowChangeDialog: React.FC<RowChangeDialogProps> = ({
  open, onClose, title, picture, name, caption = null, suggestion = null, value, onChange, seen = null,
  newVariety = null, sharedBed, bed, onBed, beds, removeLabel, onRemove, doneDisabled, idPrefix, showBed = true, doneLabel, onDone, cancelable = false,
}) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  const bedValue = bed === null ? SAME : bed.kind === 'new' ? NEW : String(bed.id);

  return (
    <BottomPanel open={open} onOpenChange={(next) => { if (!next) onClose(); }} title={title}>
      <div className="-mx-1 flex max-h-[70dvh] flex-col gap-3 overflow-y-auto px-1 pb-1">
        <div className="flex items-center gap-3">
          <span className="shrink-0" aria-hidden="true">{picture}</span>
          <p className="min-w-0 text-sm leading-snug">
            {name}
            {caption && <span className="block text-xs text-muted-foreground">{caption}</span>}
          </p>
        </div>
        {suggestion}
        <PlantIdentityFields value={value} onChange={onChange} idPrefix={idPrefix} search seen={seen} />
        {newVariety && (
          <div>
            <label htmlFor={`${idPrefix}-variety-name`} className="mb-1 block text-xs text-muted-foreground">{t('packets.newVariety')}</label>
            <Input
              id={`${idPrefix}-variety-name`}
              value={newVariety.value}
              onChange={(event) => newVariety.onChange(event.target.value)}
              maxLength={limits.VARIETY_NAME_MAX}
            />
          </div>
        )}
        {showBed && (<div>
          <label htmlFor={`${idPrefix}-bed`} className="mb-1 block text-xs text-muted-foreground">{t('packets.bed')}</label>
          <NativeSelect
            id={`${idPrefix}-bed`}
            value={bedValue}
            onChange={(event) => {
              const picked = event.target.value;
              onBed(picked === SAME ? null : picked === NEW ? { kind: 'new', name: bed?.kind === 'new' ? bed.name : '' } : { kind: 'bed', id: Number(picked) });
            }}
          >
            <option value={SAME}>{t('tray.kinds.sameAsOthers', { bed: sharedBed || '…' })}</option>
            <option value={NEW}>{t('packets.newBed')}</option>
            {beds.map((each) => <option key={each.id} value={String(each.id)}>{each.name}</option>)}
          </NativeSelect>
        </div>)}
        {showBed && bed?.kind === 'new' && (
          <div>
            <label htmlFor={`${idPrefix}-bed-name`} className="mb-1 block text-xs text-muted-foreground">{t('packets.bedName')}</label>
            <Input
              id={`${idPrefix}-bed-name`}
              value={bed.name}
              onChange={(event) => onBed({ kind: 'new', name: event.target.value })}
              maxLength={limits.LOCATION_NAME_MAX}
            />
          </div>
        )}
        {cancelable ? (
          <ActionRow onCancel={onClose} onRemove={onRemove} removeLabel={removeLabel}>
            <Button type="button" size="lg" onClick={onDone ?? onClose} disabled={doneDisabled}>{doneLabel ?? t('packets.done')}</Button>
          </ActionRow>
        ) : (
          <div className="mt-1 flex items-center justify-between gap-2">
            <Button type="button" variant="ghost" className="h-11 px-1 text-muted-foreground" onClick={onRemove}>{removeLabel}</Button>
            <Button type="button" className="h-11 px-6" onClick={onDone ?? onClose} disabled={doneDisabled}>{doneLabel ?? t('packets.done')}</Button>
          </div>
        )}
      </div>
    </BottomPanel>
  );
};
